/*eslint-disable*/
import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { isEmpty, isObjectLike } from 'lodash';
import { Section, FilePreview } from 'ui-kit';
import { AuthEntity, VisitEntity } from '_entities';
import { AccessControl, IsAzAccount, IsMassAccount } from 'modules';
import FileTagList from 'modules/Media/tags/FileTagList';
import { removeHtmlTags, getDataFromInclude, formatDateTimeZone, formatTimeWithZone, extractJsonObjects } from 'utils/custom';
import moment from 'moment';
import { URLS, PERMISSIONS } from '_constants';
import { SURVEY_QUESTION_TYPE } from '../../constants';
import ViolationView from '../ViolationView';
import * as Styled from '../../styles';
import { InspectorModal } from 'modules/Modals';

const { getVisitData, getVisitInfo } = VisitEntity.selectors;
const { getTimeZone } = AuthEntity.selectors;

function VisitViewDetails({ noViolation, isInspectorMode }) {
  const { data: visitData, included: visitIncluded } = useSelector(getVisitData);
  const { sectionTitle } = useSelector(getVisitInfo);
  const timeZone = useSelector(getTimeZone);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const visitAttributes = visitData && visitData.attributes;
  const questions = visitIncluded && getDataFromInclude(visitIncluded, 'Form')?.attributes.form_json.pages;
  const userCreatorId = visitData?.relationships.user.data?.id;
  const userModifiedById = visitData?.relationships.modified_by.data?.id;
  const userCreator = visitIncluded && getDataFromInclude(visitIncluded, 'User', userCreatorId);
  const userModifiedBy = visitIncluded && getDataFromInclude(visitIncluded, 'User', userModifiedById);
  const isMassAccount = IsMassAccount();
  const isAzAccount = IsAzAccount();
  const hasViolationInfo = isMassAccount && visitAttributes?.is_violation_open_status !== null && !noViolation;
  // Shared with the retailer notes, so both name the account's time zone the same way.
  const getFormattedTime = (date) => formatTimeWithZone(date, timeZone) || '';

  const submittedDate = formatDateTimeZone(visitAttributes?.submitted, timeZone, 'L');
  const modifiedDate = formatDateTimeZone(visitAttributes?.modified, timeZone, 'L');

  const getTitle = useCallback((type, title) => {
    switch (type) {
      case SURVEY_QUESTION_TYPE.HTML:
      case SURVEY_QUESTION_TYPE.COMMENT: {
        return removeHtmlTags(title);
      }

      default:
        return title;
    }
  }, []);

  const handleItemClick = useCallback(
    (item) => {
      if (isInspectorMode) {
        setSelectedItem(item);
        setModalOpen(true);
      }
    },
    [isInspectorMode],
  );

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setSelectedItem(null);
  }, []);

  const getViewData = useMemo(() => {
    if (!visitData) return null;
    const EXCEPTIONS = ['any_notes', 'field_notes', 'thanks'];

    const getChoicesValues = (arr) => {
      if (!arr) return {};
      return arr.reduce((acc, item) => {
        acc[item.value] = item.text;
        return acc;
      }, {});
    };

    const getTextValue = ({ attributesValue, attributes, choicesObject, fieldName }) => {
      if (choicesObject[attributesValue] && choicesObject[attributesValue] !== null)
        return choicesObject[attributesValue];
      if (typeof attributes[fieldName] === 'string') return attributes[fieldName];
      const metadataField = attributes?.metadata?.[fieldName];
      if (metadataField && metadataField !== null) return metadataField;
      return 'No response';
    };

    const getValue = ({ name, type, choices, inputType }) => {
      let result = '';
      // some fields in forms started form capital letter but in attributes is in lowercase
      let visitAttributesValue = visitAttributes[name] || visitAttributes[name.toLowerCase()];
      if (visitAttributesValue === undefined) {
        const visitAttributesMetadata = visitAttributes.metadata;
        let currentValue = visitAttributesMetadata[name] || visitAttributesMetadata[name.toLowerCase()];
        // Convert some values in capital letters in string 'FALSE', 'TRUE' into boolean
        const upperCaseValues = {
          TRUE: true,
          FALSE: false,
        };
        if (Object.keys(upperCaseValues).includes(currentValue)) {
          currentValue = upperCaseValues[currentValue];
        }
        visitAttributesValue = currentValue;
      }

      switch (type) {
        case SURVEY_QUESTION_TYPE.TEXT: {
          if (inputType === 'date' && visitAttributesValue) {
            result = moment(visitAttributesValue).format('MM/DD/YYYY');
            return result;
          }
          if (name === 'fda_letter_link') {
            result = <a href={visitAttributesValue}>{visitAttributesValue}</a>;
          }
          if (choices && typeof choices === 'object') {
            const choicesObj = getChoicesValues(choices);
            const resultText = getTextValue({
              attributesValue: visitAttributesValue,
              attributes: visitAttributes,
              choicesObject: choicesObj,
              fieldName: name,
            });
            result = resultText || 'No response';
          } else {
            result = visitAttributesValue || 'No response';
          }
          return result;
        }
        case SURVEY_QUESTION_TYPE.BOOLEAN: {
          return visitAttributesValue === true ? 'Yes' : 'No';
        }

        case SURVEY_QUESTION_TYPE.DROPDOWN:
        case SURVEY_QUESTION_TYPE.RADIOGROUP: {
          const choicesObj = getChoicesValues(choices);
          const resultText = getTextValue({
            attributesValue: visitAttributesValue,
            attributes: visitAttributes,
            choicesObject: choicesObj,
            fieldName: name,
          });

          result = resultText || 'No response';
          return result;
        }

        case SURVEY_QUESTION_TYPE.CHECKBOX: {
          const choicesObj = getChoicesValues(choices);

          if (Array.isArray(visitAttributesValue)) {
            visitAttributesValue.forEach((item, i) => {
              if (i > 0) {
                result += `, ${choicesObj[item] || 'No response'}`;
              } else {
                result += choicesObj[item] || 'No response';
              }
            });
          } else {
            result = choicesObj[visitAttributesValue] || 'No response';
          }
          return result;
        }

        case SURVEY_QUESTION_TYPE.HTML: {
          // In case if value is object it return string with capitalized keys and values separated by comma
          // @return {string}
          // @example {foo: 1, bar: 2, baz: 3}
          // return 'Foo: 1, Bar: 2, Baz: 3'
          if (isObjectLike(visitAttributesValue)) {
            const keys = Object.keys(visitAttributesValue);
            keys.forEach((key, i) => {
              const capitalizedKey = key.replace(/\w\S*/g, function (txt) {
                return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
              });
              result += `${i > 0 ? ', ' : ''}${capitalizedKey}: ${visitAttributesValue[key]}`;
            });
            break;
          }
          result = visitAttributesValue || 'No response';
          break;
        }

        case SURVEY_QUESTION_TYPE.FILE: {
          const filePath = visitAttributesValue?.file_path || visitAttributesValue;
          if (!filePath) {
            result = 'No response';
            break;
          }

          // Tags ride along inside the file value itself, so every file question shows the ones it has.
          result = (
            <>
              <FilePreview value={visitAttributesValue} zoom />
              <FileTagList value={visitAttributesValue} />
            </>
          );
          break;
        }

        default:
          result = visitAttributesValue || 'No response';
          break;
      }
      return result;
    };

    return questions?.reduce((acc, item) => {
      if (item.name) {
        acc.push({
          isPageTitle: true,
          pageTitle: item.name,
        });
      }

      item.questions?.forEach((innerItem) => {
        const { title, html, type, choices, name, inputType } = innerItem;
        if (EXCEPTIONS.includes(name) || html) return acc;
        acc.push({
          ...innerItem,
          title: getTitle(type, title),
          value: getValue({ name, type, choices, inputType }),
        });
        return acc;
      });

      item.elements?.forEach((innerItem) => {
        const { name, type, title, html, choices, inputType } = innerItem;
        if (EXCEPTIONS.includes(name) || html) return acc;
        const value = getValue({ name, type, choices, inputType });
        if (!value || html) return 'No response';
        acc.push({
          ...innerItem,
          title: getTitle(type, title),
          value,
        });
        return acc;
      });

      return acc;
    }, []);
  }, [visitData]);

  const formatedSubmittedTime = getFormattedTime(visitAttributes?.submitted);
  const localSumbittedTime = formatedSubmittedTime.split(' ').slice(0, 2).join('');

  return (
    <>
      <Styled.RowWrapper>
        <Section title={isAzAccount ? sectionTitle.replace('Enforcement', 'Inspection') : sectionTitle}>
          <Styled.VerticalBorder />
          {getViewData && (
            <Styled.Dl>
              {getViewData.map((item, i) => {
                const { value, title, name, visibleIf, pageTitle } = item;
                const [isObject] = extractJsonObjects(value);
                const WrapperComponent = isInspectorMode ? Styled.InspectorItemWrapper : Styled.ItemWrapper;

                if (pageTitle) {
                  return <Styled.TableTitle>{pageTitle}</Styled.TableTitle>;
                }

                if (!isEmpty(isObject)) {
                  return (
                    <WrapperComponent
                      key={`${title}${i}`}
                      onDoubleClick={() => handleItemClick(item)}
                      onTouchEnd={() => handleItemClick(item)}
                    >
                      <Styled.Dt>
                        {title}
                        {isInspectorMode && (
                          <Styled.InspectorMetadata>
                            <div>name: {name}</div>
                            {visibleIf && <div>visibleIf: {visibleIf}</div>}
                          </Styled.InspectorMetadata>
                        )}
                      </Styled.Dt>
                      <Styled.Dd>
                        <Styled.SubTable>
                          <tbody>
                            {Object.entries(isObject).map(([fieldKey, fieldValue]) => (
                              <tr key={fieldKey}>
                                <th>{fieldKey || '-'}</th>
                                <td key={fieldValue}>{fieldValue || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Styled.SubTable>
                      </Styled.Dd>
                      {i !== getViewData.length - 1 && <Styled.HorizontalBorder subTable />}
                    </WrapperComponent>
                  );
                }

                return (
                  <WrapperComponent
                    key={`${title}${i}`}
                    onDoubleClick={() => handleItemClick(item)}
                    onTouchEnd={() => handleItemClick(item)}
                  >
                    <Styled.Dt>
                      {title}
                      {isInspectorMode && (
                        <Styled.InspectorMetadata>
                          <div>name: {name}</div>
                          {visibleIf && <div>visibleIf: {visibleIf}</div>}
                        </Styled.InspectorMetadata>
                      )}
                    </Styled.Dt>
                    <Styled.Dd>{value || '-'}</Styled.Dd>
                    {i !== getViewData.length - 1 && <Styled.HorizontalBorder />}
                  </WrapperComponent>
                );
              })}
            </Styled.Dl>
          )}
        </Section>
        <Section title="Submission information" isSubmission>
          <Styled.Dl isSubmission>
            <Styled.SubmissionItemWrapper>
              <Styled.Dt>Submitted date</Styled.Dt>
              <Styled.Dd>{submittedDate}</Styled.Dd>
            </Styled.SubmissionItemWrapper>
            <Styled.SubmissionItemWrapper>
              <Styled.Dt>Submitted time</Styled.Dt>
              <Styled.Dd>{localSumbittedTime === '12:00AM' ? ' - ' : formatedSubmittedTime}</Styled.Dd>
            </Styled.SubmissionItemWrapper>
            <Styled.SubmissionItemWrapper>
              <Styled.Dt>Completed by</Styled.Dt>
              <Styled.Dd>
                <Link to={`${URLS.users}/${userCreator?.id}`}>{userCreator?.attributes.email}</Link>
              </Styled.Dd>
            </Styled.SubmissionItemWrapper>
            <Styled.SubmissionItemWrapper>
              <Styled.Dt>Last modified user</Styled.Dt>
              <Styled.Dd>
                <Link to={`${URLS.users}/${userModifiedBy?.id}`}>{userModifiedBy?.attributes.email}</Link>
              </Styled.Dd>
            </Styled.SubmissionItemWrapper>
            <Styled.SubmissionItemWrapper>
              <Styled.Dt>Last modified date</Styled.Dt>
              <Styled.Dd>{modifiedDate}</Styled.Dd>
            </Styled.SubmissionItemWrapper>
          </Styled.Dl>
        </Section>
      </Styled.RowWrapper>
      {hasViolationInfo && visitAttributes && (
        <AccessControl permission={PERMISSIONS.VIEW_OTHER_VIOLATION_INFO}>
          <ViolationView />
        </AccessControl>
      )}
      <InspectorModal isOpen={modalOpen} onClose={closeModal} data={selectedItem} />
    </>
  );
}

VisitViewDetails.propTypes = {
  noViolation: PropTypes.bool,
  isInspectorMode: PropTypes.bool,
};

VisitViewDetails.defaultProps = {
  noViolation: false,
  isInspectorMode: false,
};

export default VisitViewDetails;
